import React, { forwardRef, type Ref, useEffect, useRef, useState } from "react";

import { Label } from "@/components/ui/Typography";

import { cn } from "@/utils/helpers/classnames";
import { TrashIcon } from "lucide-react";

export interface FileUploadProps {
  label?: string;
  labelIcon?: React.ReactNode;
  required?: boolean;
  onChange?: (files: File[] | null) => void;
  showPreview?: boolean;
  error?: string;
  height?: string;
  placeholder?: string;
  disabled?: boolean;
  $dataId?: string;
  containerClassName?: string;
  componentClassName?: string;
  $maxSizeMB?: number;
  previewClassName?: string;
  children?: React.ReactNode;
  allowedFileTypes?: string[];
  multiple?: boolean;
  accept?: string; // New prop for custom accept value
}

const FileUpload = forwardRef(
  (
    {
      label,
      labelIcon,
      onChange,
      showPreview = true,
      containerClassName,
      componentClassName,
      previewClassName,
      error: formError,
      placeholder = "Click or drag document(s) here",
      disabled,
      $maxSizeMB,
      children,
      required,
      allowedFileTypes,
      multiple = false,
      accept,
      ...props
    }: FileUploadProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string>("");
    const [isDragging, setIsDragging] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      setError(formError || "");
    }, [formError]);

    const verifyFile = (file: File): boolean => {
      if ($maxSizeMB && file.size > $maxSizeMB * 1024 * 1024) {
        setError(`File size exceeds ${$maxSizeMB}MB`);
        return false;
      }

      const allMimeTypes = [
        "image/*",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation"
      ];

      const allowedMimeTypes = allowedFileTypes ?? allMimeTypes;
      const isAllowed = allowedMimeTypes.some((mime) => {
  if (mime.endsWith("/*")) {
    const baseType = mime.replace("/*", "");
    return file.type.startsWith(`${baseType}/`);
  }

  return file.type === mime;
});

      if (!isAllowed) {
        setError("Invalid file type. Please select a valid file.");
        return false;
      }

      return true;
    };

    const handleFilesSelected = (selectedFiles: FileList | null) => {
      if (!selectedFiles) return;

      const validFiles: File[] = [];
      Array.from(selectedFiles).forEach((file) => {
        if (verifyFile(file)) validFiles.push(file);
      });

      if (validFiles.length > 0) {
        const updatedFiles = multiple ? [...files, ...validFiles] : [validFiles[0]];
        setFiles(updatedFiles);
        onChange?.(updatedFiles);
        setError("");
      }

      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = "";
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      handleFilesSelected(e.target.files);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer.files;
      handleFilesSelected(droppedFiles);
    };

    const removeFile = (index: number) => {
      const updated = files.filter((_, i) => i !== index);
      setFiles(updated);
      onChange?.(updated.length ? updated : null);

      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = "";
      }
    };

    const acceptValue = accept ?? allowedFileTypes?.join(",");


    return (
      <div className={cn('relative flex h-auto w-full cursor-pointer flex-col', componentClassName)}>
        {label ? (
          <label
            className={cn(
              "mb-2 block text-sm leading-none text-text-secondary",
              required && "after:text-error after:content-['*']"
            )}
          >
            {labelIcon ? labelIcon : null}
            {label}
          </label>
        ) : null}

        <div
          ref={ref}
          onClick={() => !disabled && hiddenInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          data-disabled={disabled}
          className={cn(
            containerClassName,
            "transition-all",
            "flex flex-col justify-center rounded-lg border border-dashed p-4",
            error ? "border-error" : "border-gray-300",
            disabled ? "bg-disabledInput cursor-not-allowed" : "hover:border-primary",
            isDragging && "bg-primary/5 border-primary"
          )}
          {...props}
        >
          {/* ✅ File Previews */}
          {showPreview && files.length > 0 ? (
            <div className={cn("flex h-full w-full flex-col gap-2 overflow-y-auto", previewClassName)}>
              {files.map((file, index) => (
                <div
                  key={index}
                  className='flex items-center justify-between gap-3 rounded border border-gray-300 bg-white p-2'
                >
                  <div className='flex items-center gap-2'>
                    {file.type.startsWith("image/") && (
                      <img src={URL.createObjectURL(file)} alt='Preview' className='h-8 w-8 rounded object-cover' />
                    )}
                    <div className='flex flex-col'>
                      <span className='break-all text-sm'>{file.name}</span>
                      <Label className='text-xs text-text-secondary'>{(file.size / (1024 * 1024)).toFixed(2)}MB</Label>
                    </div>
                  </div>

                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className='shrink-0 cursor-pointer p-2 bg-[#FAE5E4] rounded'
                  >
                    <TrashIcon
                      className='shrink-0 cursor-pointer'
                      stroke="#DE534E"
                      onClick={() => {
                        removeFile(index);
                      }}
                    />
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className='m-auto px-4 text-center text-sm text-text-secondary'>{placeholder}</div>
          )}

          {/* Custom Children */}
          {(!showPreview || files.length === 0) && children}

          {/* Hidden Input */}
          <input
            ref={hiddenInputRef}
            type='file'
            multiple={multiple}
            disabled={disabled}
            onChange={handleInputChange}
            accept={acceptValue}
            className='hidden'
          />
        </div>

        {error ? <span className='absolute -bottom-4 text-xs text-[#eb5757]'>{error}</span> : null}
      </div>
    );
  }
);

FileUpload.displayName = "FileUpload";
export default FileUpload;
